import React, { useState, useEffect } from 'react';
import { Code2, PlayCircle, CheckCircle2, Lock, ChevronRight, X, ChevronDown, Clock } from 'lucide-react';
import { getLessons, type Lesson } from '../services/lessons';
import { WHATSAPP_GROUP_LINK, getContentDeadline } from '../config/constants';
import { useCountdown } from '../hooks/useCountdown';

// Chave para armazenar o progresso no localStorage
const PROGRESS_STORAGE_KEY = 'jornadajava_progress';

const LessonsPage: React.FC = () => {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [expandedLesson, setExpandedLesson] = useState<Lesson | null>(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState<Lesson | null>(null);

  // Use the deadline helper function
  const timeLeft = useCountdown(getContentDeadline());

  useEffect(() => {
    loadLessons();
  }, []);

  const loadLessons = async () => {
    try {
      setIsLoading(true);
      const data = await getLessons();
      
      // Recupera o progresso salvo no localStorage
      const savedProgress = localStorage.getItem(PROGRESS_STORAGE_KEY);
      const completedLessons = savedProgress ? JSON.parse(savedProgress) as number[] : [];
      
      // Combina os dados do Supabase com o progresso local
      const lessonsWithProgress = data.map(lesson => ({
        ...lesson,
        is_completed: completedLessons.includes(lesson.id)
      }));
      
      setLessons(lessonsWithProgress);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar as aulas. Por favor, tente novamente.');
      console.error('Error loading lessons:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Calcula o progresso real
  const progress = {
    completed: lessons.filter(lesson => lesson.is_completed).length,
    total: lessons.length,
    percentage: 0
  };
  progress.percentage = (progress.completed / progress.total) * 100;

  const handleLessonClick = (lesson: Lesson) => {
    if (lesson.is_available && lesson.video_id) {
      setShowDescriptionModal(lesson);
    }
  };

  const handleWatchLesson = (lesson: Lesson) => {
    if (lesson.is_available && lesson.video_id) {
      setSelectedLesson(lesson);
    }
  };

  const toggleLesson = (lesson: Lesson) => {
    setExpandedLesson(expandedLesson?.id === lesson.id ? null : lesson);
  };

  const toggleLessonCompletion = (e: React.MouseEvent, lessonId: number) => {
    e.stopPropagation(); // Previne que o dropdown feche ao clicar no botão
    
    // Adiciona classe de animação ao elemento clicado
    const button = e.currentTarget as HTMLButtonElement;
    button.classList.add('scale-bounce');
    setTimeout(() => button.classList.remove('scale-bounce'), 500);

    // Atualiza o estado localmente
    const updatedLessons = lessons.map(lesson =>
      lesson.id === lessonId ? { ...lesson, is_completed: !lesson.is_completed } : lesson
    );
    setLessons(updatedLessons);

    // Salva o progresso no localStorage
    const completedLessons = updatedLessons
      .filter(lesson => lesson.is_completed)
      .map(lesson => lesson.id);
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(completedLessons));
  };

  // Remove a marcação automática ao fechar o vídeo
  const handleVideoClose = () => {
    setSelectedLesson(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#f2055c]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={loadLessons}
            className="bg-[#f2055c] text-white px-4 py-2 rounded hover:bg-[#d91438] transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes scaleBounce {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          .scale-bounce {
            animation: scaleBounce 0.5s ease-in-out;
          }
        `
      }} />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm sticky top-0 z-20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <Code2 className="w-8 h-8 text-[#f2055c]" />
            <h1 className="text-2xl font-bold">Java Dev Week</h1>
          </div>
        </div>
      </header>

      {/* Video Player Modal */}
      {selectedLesson && (
        <div className="fixed inset-0 bg-black/90 z-30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent">
              <h3 className="font-semibold text-lg">{selectedLesson.title}</h3>
              <button
                onClick={handleVideoClose}
                className="p-1 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="relative pt-[56.25%]">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${selectedLesson.video_id}?autoplay=1`}
                title={selectedLesson.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Video Info */}
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span>{selectedLesson.duration}</span>
                </div>
                <button
                  onClick={(e) => toggleLessonCompletion(e, selectedLesson.id)}
                  className={`group/btn flex items-center gap-1 rounded-full py-1 px-2 transition-all duration-300 ${
                    selectedLesson.is_available 
                      ? 'hover:bg-white/10 cursor-pointer' 
                      : 'cursor-not-allowed opacity-50'
                  } ${selectedLesson.is_completed ? 'bg-[#f2055c]/10' : ''}`}
                  disabled={!selectedLesson.is_available}
                  title={selectedLesson.is_available ? 'Alternar conclusão da aula' : 'Aula bloqueada'}
                >
                  {selectedLesson.is_completed ? (
                    <CheckCircle2 className="w-5 h-5 text-[#f2055c]" />
                  ) : selectedLesson.is_available ? (
                    <div className="w-5 h-5 rounded-full border-2 border-[#f2055c] group-hover/btn:bg-[#f2055c]/10" />
                  ) : (
                    <Lock className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>
              <p className="text-sm text-gray-300">
                {selectedLesson.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {showDescriptionModal && (
        <div className="fixed inset-0 bg-black/90 z-30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white/5 rounded-lg overflow-hidden shadow-2xl border border-white/10">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-semibold text-lg">{showDescriptionModal.title}</h3>
              <button
                onClick={() => setShowDescriptionModal(null)}
                className="p-1 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-gray-300 mb-6">
                {showDescriptionModal.description}
              </p>
              
              {showDescriptionModal.topics && showDescriptionModal.topics.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-[#f2055c]">O que você vai aprender:</h4>
                  {showDescriptionModal.topics.map((topic, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-300">
                      <div className="w-1 h-1 bg-[#f2055c] rounded-full" />
                      {topic}
                    </div>
                  ))}
                </div>
              )}

              {showDescriptionModal.is_available && showDescriptionModal.video_id && (
                <button
                  onClick={() => {
                    setShowDescriptionModal(null);
                    handleWatchLesson(showDescriptionModal);
                  }}
                  className="mt-6 w-full flex items-center justify-center gap-2 bg-[#f2055c] text-white font-semibold px-4 py-3 rounded hover:bg-[#d91438] transition-colors"
                >
                  <PlayCircle className="w-5 h-5" />
                  Assistir Aula
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Progress Section */}
        <div className="mb-12 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Seu Progresso</h2>
            <div className="text-sm text-gray-400">
              {progress.completed} de {progress.total} aulas concluídas
            </div>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#f2055c] transition-all duration-500"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>

        {/* Lessons Grid */}
        <div className="space-y-8 max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold">Aulas Disponíveis</h2>
          
          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="space-y-6">
              {lessons.map((lesson) => (
                <div
                  key={lesson.id}
                  className="relative"
                >
                  {/* Card */}
                  <div className="relative aspect-[16/9] bg-black rounded-lg overflow-hidden">
                    {/* Thumbnail */}
                    <div className="relative w-full h-full">
                      {lesson.video_id ? (
                        <>
                          <img
                            src={`https://img.youtube.com/vi/${lesson.video_id}/maxresdefault.jpg`}
                            alt={lesson.title}
                            className={`w-full h-full object-cover ${!lesson.is_available ? 'opacity-50' : ''}`}
                          />
                          {/* Play Button */}
                          {lesson.is_available ? (
                            <button 
                              className="absolute inset-0 flex items-center justify-center bg-black/60 cursor-pointer"
                              onClick={() => lesson.is_available && lesson.video_id && handleWatchLesson(lesson)}
                            >
                              <div className="flex flex-col items-center gap-2">
                                <PlayCircle className="w-16 h-16 text-white" />
                                <span className="text-white font-medium">Assistir</span>
                              </div>
                            </button>
                          ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
                              <Lock className="w-16 h-16 text-[#f2055c]" />
                              <span className="text-white font-medium mt-4">Aula Bloqueada</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-[#f2055c]/20 to-[#f20587]/20">
                          <Lock className="w-16 h-16 text-[#f2055c]" />
                          <span className="text-white font-medium">Aula Bloqueada</span>
                        </div>
                      )}

                      {/* Lesson Info */}
                      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Clock className="w-4 h-4" />
                            <span>{lesson.duration}</span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              lesson.is_available && toggleLessonCompletion(e, lesson.id);
                            }}
                            className={`flex items-center gap-1 rounded-full py-1 px-2 transition-all duration-300 ${
                              lesson.is_available 
                                ? 'hover:bg-white/10 cursor-pointer' 
                                : 'cursor-not-allowed opacity-50'
                            } ${lesson.is_completed ? 'bg-[#f2055c]/10' : ''}`}
                            disabled={!lesson.is_available}
                            title={lesson.is_available ? 'Alternar conclusão da aula' : 'Aula bloqueada'}
                          >
                            {lesson.is_completed ? (
                              <CheckCircle2 className="w-5 h-5 text-[#f2055c]" />
                            ) : lesson.is_available ? (
                              <div className="w-5 h-5 rounded-full border-2 border-[#f2055c]" />
                            ) : (
                              <Lock className="w-5 h-5 text-gray-500" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description Panel - Always Visible on Mobile */}
                  <div className="mt-2 bg-black/95 rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-2">{lesson.title}</h3>
                    <p className="text-sm text-gray-300 mb-4">
                      {lesson.description}
                    </p>
                    
                    {lesson.topics && lesson.topics.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-semibold text-[#f2055c] text-sm">O que você vai aprender:</h4>
                        {lesson.topics.map((topic, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-gray-300">
                            <div className="w-1 h-1 bg-[#f2055c] rounded-full" />
                            {topic}
                          </div>
                        ))}
                      </div>
                    )}

                    {lesson.is_available && lesson.video_id && (
                      <button
                        onClick={() => handleWatchLesson(lesson)}
                        className="mt-4 w-full flex items-center justify-center gap-2 bg-[#f2055c] text-white font-semibold px-4 py-2 rounded hover:bg-[#d91438] transition-colors"
                      >
                        <PlayCircle className="w-5 h-5" />
                        Assistir Aula
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8 w-full">
            {lessons.map((lesson) => (
              <div
                key={lesson.id}
                className="relative group"
                onMouseEnter={() => setExpandedLesson(lesson)}
                onMouseLeave={() => setExpandedLesson(null)}
              >
                {/* Card */}
                <div className="relative aspect-[16/9] bg-black rounded-lg overflow-hidden transition-all duration-300 group-hover:scale-105">
                  {/* Thumbnail */}
                  <div className="relative w-full h-full">
                    {lesson.video_id ? (
                      <>
                        <img
                          src={`https://img.youtube.com/vi/${lesson.video_id}/maxresdefault.jpg`}
                          alt={lesson.title}
                          className={`w-full h-full object-cover ${!lesson.is_available ? 'opacity-50' : ''}`}
                        />
                        {/* Play Button */}
                        {lesson.is_available ? (
                          <button 
                            className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={() => lesson.is_available && lesson.video_id && handleWatchLesson(lesson)}
                          >
                            <div className="flex flex-col items-center gap-2">
                              <PlayCircle className="w-16 h-16 text-white" />
                              <span className="text-white font-medium">Assistir</span>
                            </div>
                          </button>
                        ) : (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
                            <Lock className="w-16 h-16 text-[#f2055c]" />
                            <span className="text-white font-medium mt-4">Aula Bloqueada</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-[#f2055c]/20 to-[#f20587]/20">
                        <Lock className="w-16 h-16 text-[#f2055c]" />
                        <span className="text-white font-medium">Aula Bloqueada</span>
                      </div>
                    )}

                    {/* Lesson Info */}
                    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span>{lesson.duration}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            lesson.is_available && toggleLessonCompletion(e, lesson.id);
                          }}
                          className={`flex items-center gap-1 rounded-full py-1 px-2 transition-all duration-300 ${
                            lesson.is_available 
                              ? 'hover:bg-white/10 cursor-pointer' 
                              : 'cursor-not-allowed opacity-50'
                          } ${lesson.is_completed ? 'bg-[#f2055c]/10' : ''}`}
                          disabled={!lesson.is_available}
                          title={lesson.is_available ? 'Alternar conclusão da aula' : 'Aula bloqueada'}
                        >
                          {lesson.is_completed ? (
                            <CheckCircle2 className="w-5 h-5 text-[#f2055c]" />
                          ) : lesson.is_available ? (
                            <div className="w-5 h-5 rounded-full border-2 border-[#f2055c]" />
                          ) : (
                            <Lock className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description Panel */}
                <div className={`absolute left-0 right-0 top-full mt-2 bg-black/95 rounded-lg p-4 transition-all duration-300 transform ${
                  expandedLesson?.id === lesson.id 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-2 pointer-events-none'
                } z-10 shadow-xl`}>
                  <h3 className="font-semibold text-lg mb-2">{lesson.title}</h3>
                  <p className="text-sm text-gray-300 mb-4">
                    {lesson.description}
                  </p>
                  
                  {lesson.topics && lesson.topics.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-[#f2055c] text-sm">O que você vai aprender:</h4>
                      {lesson.topics.map((topic, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-300">
                          <div className="w-1 h-1 bg-[#f2055c] rounded-full" />
                          {topic}
                        </div>
                      ))}
                    </div>
                  )}

                  {lesson.is_available && lesson.video_id && (
                    <button
                      onClick={() => handleWatchLesson(lesson)}
                      className="mt-4 w-full flex items-center justify-center gap-2 bg-[#f2055c] text-white font-semibold px-4 py-2 rounded hover:bg-[#d91438] transition-colors"
                    >
                      <PlayCircle className="w-5 h-5" />
                      Assistir Aula
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* WhatsApp Group CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-[#25D366]/20 to-[#128C7E]/20 rounded-xl p-8 backdrop-blur-sm border border-[#25D366]/30">
            <h3 className="text-2xl font-bold mb-3">Entre para nosso Grupo VIP no WhatsApp!</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Participe do nosso grupo exclusivo e tenha acesso a conteúdos extras, tire suas dúvidas e interaja com outros devs!
            </p>
            <a
              href={WHATSAPP_GROUP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Entrar no Grupo VIP
            </a>
          </div>
        </div>

        {/* Countdown Timer */}
        <div className="mt-12">
          <div className="bg-gradient-to-r from-[#f2055c]/20 to-[#f20587]/20 rounded-lg p-8 backdrop-blur-sm border border-[#f2055c]/30">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Clock className="w-8 h-8 text-[#f2055c]" />
              <h2 className="text-2xl font-bold text-center">Tempo Restante para o Encerramento</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-black/30 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-[#f2055c] mb-1">{String(timeLeft.days).padStart(2, '0')}</div>
                <div className="text-sm text-gray-400">Dias</div>
              </div>
              <div className="bg-black/30 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-[#f2055c] mb-1">{String(timeLeft.hours).padStart(2, '0')}</div>
                <div className="text-sm text-gray-400">Horas</div>
              </div>
              <div className="bg-black/30 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-[#f2055c] mb-1">{String(timeLeft.minutes).padStart(2, '0')}</div>
                <div className="text-sm text-gray-400">Minutos</div>
              </div>
              <div className="bg-black/30 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-[#f2055c] mb-1">{String(timeLeft.seconds).padStart(2, '0')}</div>
                <div className="text-sm text-gray-400">Segundos</div>
              </div>
            </div>

            <div className="mt-6 text-center text-gray-400">
              Não perca tempo! Aproveite o conteúdo antes que o acesso seja encerrado.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LessonsPage;